/**
 * Controller for main menu, settings, multiplayer menu, and lobby UI
 */

import { apiClient, UserSettings } from '../api/client';
import { wsClient, PlayerInfo, GameResult } from '../multiplayer/WebSocketClient';
import { soundManager } from '../game/SoundManager';

export type GameMode = 'solo' | 'multiplayer' | 'training';

export interface MenuCallbacks {
  onStartSoloGame: () => void;
  onStartTrainingGame: () => void;
  onStartMultiplayerGame: (roomId: number, seed: string, objectsMap: any[], players: PlayerInfo[], startAt: number) => void;
  onShowLeaderboard: () => void;
}

// Player colors
const PLAYER_COLORS: Record<string, string> = {
  red: '#e04040',
  blue: '#4080e0',
  green: '#40e040',
  yellow: '#e0e040',
  purple: '#a040e0'
};

// Simplified room info we track locally
interface CurrentRoomInfo {
  id: number;
  code: string;
  max_players: number;
}

export class MenuController {
  private callbacks: MenuCallbacks;

  // Current state
  private currentRoom: CurrentRoomInfo | null = null;
  private isHost: boolean = false;
  private isReady: boolean = false;
  private myUserId: number = 0;
  private players: PlayerInfo[] = [];
  private intentionalDisconnect: boolean = false;
  private settings: UserSettings = {
    sound_enabled: true,
    sound_volume: 0.5,
    preferred_plane_color: 'red'
  };

  // DOM elements
  private mainMenuOverlay!: HTMLElement;
  private settingsOverlay!: HTMLElement;
  private mpMenuOverlay!: HTMLElement;
  private lobbyOverlay!: HTMLElement;
  private mpResultOverlay!: HTMLElement;

  constructor(callbacks: MenuCallbacks) {
    this.callbacks = callbacks;
    this.bindElements();
    this.setupEventListeners();
    this.setupWebSocketHandlers();
  }

  private bindElements(): void {
    this.mainMenuOverlay = document.getElementById('main-menu')!;
    this.settingsOverlay = document.getElementById('settings-overlay')!;
    this.mpMenuOverlay = document.getElementById('mp-menu')!;
    this.lobbyOverlay = document.getElementById('lobby-overlay')!;
    this.mpResultOverlay = document.getElementById('mp-result')!;
  }

  private setupEventListeners(): void {
    // Main menu buttons
    document.getElementById('menu-solo')?.addEventListener('click', () => {
      this.hideMainMenu();
      this.callbacks.onStartSoloGame();
    });

    document.getElementById('menu-training')?.addEventListener('click', () => {
      this.hideMainMenu();
      this.callbacks.onStartTrainingGame();
    });

    document.getElementById('menu-multiplayer')?.addEventListener('click', () => {
      this.hideMainMenu();
      this.showMpMenu();
    });

    document.getElementById('menu-leaderboard')?.addEventListener('click', () => {
      this.hideMainMenu();
      this.callbacks.onShowLeaderboard();
    });

    document.getElementById('menu-settings')?.addEventListener('click', () => {
      this.showSettings();
    });

    // Settings
    document.getElementById('settings-close')?.addEventListener('click', () => {
      this.hideSettings();
    });

    document.getElementById('volume-slider')?.addEventListener('input', (e) => {
      const value = parseInt((e.target as HTMLInputElement).value);
      document.getElementById('volume-value')!.textContent = `${value}%`;
      this.settings.sound_volume = value / 100;
      soundManager.setVolume(this.settings.sound_volume);
      this.saveSettings();
    });

    document.getElementById('sound-on')?.addEventListener('click', () => {
      this.setSoundEnabled(true);
    });

    document.getElementById('sound-off')?.addEventListener('click', () => {
      this.setSoundEnabled(false);
    });

    // Plane color selection
    document.querySelectorAll('.color-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const color = (e.target as HTMLElement).dataset.color;
        if (color) {
          this.setPlaneColor(color);
        }
      });
    });

    document.getElementById('settings-logout')?.addEventListener('click', () => {
      this.hideSettings();
      apiClient.logout();
      window.location.reload();
    });

    // Multiplayer menu
    document.getElementById('mp-find-match')?.addEventListener('click', () => this.findMatch());
    document.getElementById('mp-create-private')?.addEventListener('click', () => this.createRoom('private'));
    document.getElementById('mp-create-public')?.addEventListener('click', () => this.createRoom('public'));
    document.getElementById('mp-join-btn')?.addEventListener('click', () => this.joinByCode());
    document.getElementById('mp-back')?.addEventListener('click', () => {
      this.hideMpMenu();
      this.showMainMenu();
    });

    // Lobby
    document.getElementById('lobby-ready')?.addEventListener('click', () => this.toggleReady());
    document.getElementById('lobby-start')?.addEventListener('click', () => this.startGame());
    document.getElementById('lobby-leave')?.addEventListener('click', () => this.leaveLobby());

    // MP Result
    document.getElementById('mp-play-again')?.addEventListener('click', () => {
      if (this.isHost) {
        // Host requests rematch on same server
        wsClient.requestRematch();
      } else {
        // Non-host goes back to multiplayer menu
        this.hideMpResult();
        this.intentionalDisconnect = true;
        wsClient.disconnect();
        this.currentRoom = null;
        this.intentionalDisconnect = false;
        this.showMpMenu();
      }
    });
    document.getElementById('mp-to-menu')?.addEventListener('click', () => {
      this.hideMpResult();
      this.intentionalDisconnect = true;
      wsClient.disconnect();
      this.currentRoom = null;
      this.intentionalDisconnect = false;
      this.showMainMenu();
    });
  }

  private setupWebSocketHandlers(): void {
    wsClient.on('room_state', (msg) => {
      this.myUserId = msg.your_id;
      this.isHost = msg.host_id === this.myUserId;
      this.players = msg.players;
      this.updateLobbyUI();
    });

    wsClient.on('player_joined', (msg) => {
      // Check if player already exists (avoid duplicates)
      if (this.players.some(p => p.user_id === msg.user_id)) {
        return;
      }
      const player: PlayerInfo = {
        user_id: msg.user_id,
        nickname: msg.nickname,
        color: msg.color,
        ready: false,
        is_host: false
      };
      this.players.push(player);
      this.updateLobbyUI();
    });

    wsClient.on('player_left', (msg) => {
      this.players = this.players.filter(p => p.user_id !== msg.user_id);
      this.updateLobbyUI();
    });

    wsClient.on('player_ready', (msg) => {
      const player = this.players.find(p => p.user_id === msg.user_id);
      if (player) {
        player.ready = msg.ready;
      }
      this.updateLobbyUI();
    });

    wsClient.on('countdown', (msg) => {
      this.showCountdown(msg.seconds);
    });

    wsClient.on('game_start', (msg) => {
      this.hideLobby();
      this.callbacks.onStartMultiplayerGame(
        this.currentRoom!.id,
        msg.seed,
        msg.objects_map,
        msg.players,
        msg.start_at || Date.now()
      );
    });

    wsClient.on('game_end', (msg) => {
      this.showMpResult(msg.results, msg.winner_id);
    });

    wsClient.on('rematch_started', (_msg) => {
      // Server reset the room for a new game
      this.hideMpResult();
      this.isReady = false;
      this.showLobby();
    });

    wsClient.on('error', (msg) => {
      alert(msg.message);
    });

    wsClient.on('disconnected', () => {
      // Only show error if disconnect was not intentional
      if (this.currentRoom && !this.intentionalDisconnect) {
        alert('Соединение с сервером потеряно');
        this.leaveLobby();
      }
    });
  }

  // ========== Main Menu ==========

  async showMainMenu(): Promise<void> {
    // Update user info
    document.getElementById('menu-nickname')!.textContent = apiClient.getNickname() || 'Player';
    this.mainMenuOverlay.classList.remove('hidden');

    // Refresh balance from server
    try {
      const profile = await apiClient.getProfile();
      this.updateBalance(profile.balance);
    } catch {
      // Ignore - use cached balance
    }
  }

  hideMainMenu(): void {
    this.mainMenuOverlay.classList.add('hidden');
  }

  updateBalance(balance: number): void {
    document.getElementById('menu-balance')!.textContent = `${balance.toFixed(2)} FLY`;
  }

  // ========== Settings ==========

  async loadSettings(): Promise<void> {
    try {
      this.settings = await apiClient.getSettings();
      this.applySettings();
    } catch {
      // Use defaults
    }
  }

  private applySettings(): void {
    // Volume
    const volumeSlider = document.getElementById('volume-slider') as HTMLInputElement;
    const volumePercent = Math.round(this.settings.sound_volume * 100);
    volumeSlider.value = volumePercent.toString();
    document.getElementById('volume-value')!.textContent = `${volumePercent}%`;
    soundManager.setVolume(this.settings.sound_volume);

    // Sound toggle
    if (this.settings.sound_enabled) {
      document.getElementById('sound-on')!.classList.add('active');
      document.getElementById('sound-off')!.classList.remove('active');
    } else {
      document.getElementById('sound-off')!.classList.add('active');
      document.getElementById('sound-on')!.classList.remove('active');
      soundManager.setMuted(true);
    }

    // Plane color
    document.querySelectorAll('.color-btn').forEach(btn => {
      btn.classList.remove('active');
      if ((btn as HTMLElement).dataset.color === this.settings.preferred_plane_color) {
        btn.classList.add('active');
      }
    });
  }

  private setSoundEnabled(enabled: boolean): void {
    this.settings.sound_enabled = enabled;
    document.getElementById('sound-on')!.classList.toggle('active', enabled);
    document.getElementById('sound-off')!.classList.toggle('active', !enabled);
    soundManager.setMuted(!enabled);
    this.saveSettings();
  }

  private setPlaneColor(color: string): void {
    this.settings.preferred_plane_color = color;
    document.querySelectorAll('.color-btn').forEach(btn => {
      btn.classList.toggle('active', (btn as HTMLElement).dataset.color === color);
    });
    this.saveSettings();
  }

  private async saveSettings(): Promise<void> {
    try {
      await apiClient.updateSettings(this.settings);
    } catch {
      // Ignore errors
    }
  }

  showSettings(): void {
    this.settingsOverlay.classList.remove('hidden');
  }

  hideSettings(): void {
    this.settingsOverlay.classList.add('hidden');
  }

  getPlaneColor(): string {
    return this.settings.preferred_plane_color;
  }

  // ========== Multiplayer Menu ==========

  showMpMenu(): void {
    this.mpMenuOverlay.classList.remove('hidden');
    (document.getElementById('mp-code-input') as HTMLInputElement).value = '';
  }

  hideMpMenu(): void {
    this.mpMenuOverlay.classList.add('hidden');
  }

  private async findMatch(): Promise<void> {
    try {
      const room = await apiClient.findMatch();
      await this.enterRoom(room);
    } catch (e: any) {
      alert(e.message || 'Не удалось найти игру');
    }
  }

  private async createRoom(type: 'private' | 'public'): Promise<void> {
    try {
      const room = await apiClient.createRoom(type);
      await this.enterRoom(room);
    } catch (e: any) {
      alert(e.message || 'Не удалось создать сервер');
    }
  }

  private async joinByCode(): Promise<void> {
    const codeInput = document.getElementById('mp-code-input') as HTMLInputElement;
    const code = codeInput.value.trim().toUpperCase();
    if (!code) {
      alert('Введите код сервера');
      return;
    }

    try {
      const room = await apiClient.joinRoom(code);
      await this.enterRoom(room);
    } catch (e: any) {
      alert(e.message || 'Не удалось подключиться к серверу');
    }
  }

  private async enterRoom(roomData: { room_id: number; code: string; max_players?: number }): Promise<void> {
    this.currentRoom = {
      id: roomData.room_id,
      code: roomData.code,
      max_players: roomData.max_players || 5
    };
    this.isReady = false;
    this.players = [];

    // Connect WebSocket
    const token = apiClient.getToken();
    if (!token) {
      alert('Не авторизован');
      return;
    }

    try {
      await wsClient.connect(this.currentRoom.id, token);
      this.hideMpMenu();
      this.showLobby();
    } catch {
      alert('Не удалось подключиться к серверу');
    }
  }

  // ========== Lobby ==========

  showLobby(): void {
    document.getElementById('lobby-code')!.textContent = this.currentRoom?.code || '';
    this.updateLobbyUI();
    this.lobbyOverlay.classList.remove('hidden');
  }

  hideLobby(): void {
    this.lobbyOverlay.classList.add('hidden');
    document.getElementById('lobby-countdown')!.classList.add('hidden');
    document.getElementById('lobby-actions')!.style.display = '';
  }

  private updateLobbyUI(): void {
    const playersContainer = document.getElementById('lobby-players')!;
    playersContainer.innerHTML = '';

    // Add players
    for (const player of this.players) {
      const div = document.createElement('div');
      div.className = 'lobby-player';
      div.innerHTML = `
        <div class="color" style="background:${PLAYER_COLORS[player.color] || '#888'}"></div>
        <span class="name">${player.nickname}${player.is_host ? '<span class="host-badge">ХОСТ</span>' : ''}</span>
        <span class="status ${player.ready ? 'ready' : 'waiting'}">${player.ready ? 'Готов' : 'Ожидает'}</span>
      `;
      playersContainer.appendChild(div);
    }

    // Add waiting slots
    const maxPlayers = this.currentRoom?.max_players || 5;
    for (let i = this.players.length; i < maxPlayers; i++) {
      const div = document.createElement('div');
      div.className = 'lobby-waiting';
      div.textContent = 'Ожидание игрока...';
      playersContainer.appendChild(div);
    }

    // Update buttons
    const readyBtn = document.getElementById('lobby-ready')!;
    const startBtn = document.getElementById('lobby-start')!;

    readyBtn.textContent = this.isReady ? 'Не готов' : 'Готов';
    readyBtn.classList.toggle('active', this.isReady);

    if (this.isHost) {
      startBtn.style.display = '';
      // Can start if at least 2 players and all ready
      const canStart = this.players.length >= 2 && this.players.every(p => p.ready);
      (startBtn as HTMLButtonElement).disabled = !canStart;
    } else {
      startBtn.style.display = 'none';
    }

    // Update timer text
    const timerEl = document.getElementById('lobby-timer')!;
    timerEl.textContent = `${this.players.length}/${maxPlayers} игроков`;
  }

  private toggleReady(): void {
    this.isReady = !this.isReady;
    wsClient.setReady(this.isReady);
    this.updateLobbyUI();
  }

  private startGame(): void {
    wsClient.startGame();
  }

  private async leaveLobby(): Promise<void> {
    // Set flag to prevent "connection lost" alert
    this.intentionalDisconnect = true;
    wsClient.disconnect();

    if (this.currentRoom) {
      try {
        await apiClient.leaveRoom(this.currentRoom.id);
      } catch {
        // Ignore - might already be removed
      }
    }

    this.currentRoom = null;
    this.intentionalDisconnect = false;
    this.hideLobby();
    this.showMainMenu();
  }

  private showCountdown(seconds: number): void {
    const countdownEl = document.getElementById('lobby-countdown')!;
    const actionsEl = document.getElementById('lobby-actions')!;

    countdownEl.textContent = seconds.toString();
    countdownEl.classList.remove('hidden');
    actionsEl.style.display = 'none';
  }

  // ========== MP Result ==========

  showMpResult(results: GameResult[], _winnerId: number): void {
    const listEl = document.getElementById('mp-result-list')!;
    listEl.innerHTML = '';

    for (const result of results) {
      const isWinner = result.placement === 1;
      const isCrashed = result.finish_reason === 'crashed';
      const isMe = result.user_id === this.myUserId;

      // Show payout - winner gets bonus even if crashed
      const payoutText = result.payout > 0
        ? `+${result.payout.toFixed(2)} FLY`
        : (isCrashed ? 'Крушение' : '0 FLY');

      const div = document.createElement('div');
      div.className = `mp-result-player ${isWinner ? 'winner' : ''} ${isCrashed && !isWinner ? 'crashed' : ''}`;
      div.innerHTML = `
        <span class="rank">#${result.placement}</span>
        <div class="color" style="background:${PLAYER_COLORS[result.color] || '#888'}"></div>
        <div class="info">
          <span class="name">${result.nickname}${isMe ? ' (You)' : ''}</span>
          <span class="stats">${Math.round(result.final_distance)} m</span>
        </div>
        <span class="payout">${payoutText}</span>
      `;
      listEl.appendChild(div);

      if (isMe) {
        document.getElementById('mp-your-rank')!.textContent = `#${result.placement}`;
        document.getElementById('mp-your-distance')!.textContent = `${Math.round(result.final_distance)} m`;
        document.getElementById('mp-your-payout')!.textContent = payoutText;
      }
    }

    // Update "Play again" button text based on host status
    const playAgainBtn = document.getElementById('mp-play-again')!;
    if (this.isHost) {
      playAgainBtn.textContent = 'Реванш';
    } else {
      playAgainBtn.textContent = 'Играть ещё';
    }

    this.mpResultOverlay.classList.remove('hidden');
  }

  hideMpResult(): void {
    this.mpResultOverlay.classList.add('hidden');
    // Note: Don't disconnect here - let the button handlers do it if needed
    // This allows rematch to work without disconnecting
  }

  // ========== Utility ==========

  getCurrentRoom(): CurrentRoomInfo | null {
    return this.currentRoom;
  }

  getMyUserId(): number {
    return this.myUserId;
  }
}
